import React, { useState, useMemo } from 'react';
import { 
  Calendar as CalendarIcon, Plus, Check, Clock, ChevronLeft, ChevronRight, X, MapPin, Trash2, Edit3, Tag, AlertCircle, GripVertical, Settings,
  User, Users, Bed, UtensilsCrossed, Car, Filter,
  // Icons for selection
  Briefcase, Home, Heart, Star, Coffee, Plane, Music, Book, Gift, 
  ShoppingCart, Phone, Mail, Camera, Utensils, Dumbbell, Pill, Baby,
  Dog, Cat, Gamepad2, Tv, Laptop, Wallet, CreditCard, Building2,
  GraduationCap, Stethoscope, Scissors, Paintbrush, Wrench, Hammer
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { 
  OFFICES, BOOKINGS, SERVICES, MODULES,
  getAllBookingsForOffice, getResourcesForOffice,
} from '../data/database';
import { useI18n } from '../i18n';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Available icons for tasks and events
const AVAILABLE_ICONS = [
  { id: 'calendar', icon: CalendarIcon, label: 'Calendario' },
  { id: 'briefcase', icon: Briefcase, label: 'Lavoro' },
  { id: 'home', icon: Home, label: 'Casa' },
  { id: 'heart', icon: Heart, label: 'Salute' },
  { id: 'star', icon: Star, label: 'Importante' },
  { id: 'coffee', icon: Coffee, label: 'Pausa' },
  { id: 'car', icon: Car, label: 'Auto' },
  { id: 'plane', icon: Plane, label: 'Viaggio' },
  { id: 'music', icon: Music, label: 'Musica' },
  { id: 'book', icon: Book, label: 'Studio' },
  { id: 'gift', icon: Gift, label: 'Regalo' },
  { id: 'shopping', icon: ShoppingCart, label: 'Shopping' },
  { id: 'phone', icon: Phone, label: 'Chiamata' },
  { id: 'mail', icon: Mail, label: 'Email' },
  { id: 'camera', icon: Camera, label: 'Foto' },
  { id: 'food', icon: Utensils, label: 'Cibo' },
  { id: 'gym', icon: Dumbbell, label: 'Palestra' },
  { id: 'medical', icon: Pill, label: 'Medicina' },
  { id: 'baby', icon: Baby, label: 'Bambini' },
  { id: 'dog', icon: Dog, label: 'Animali' },
  { id: 'game', icon: Gamepad2, label: 'Giochi' },
  { id: 'tv', icon: Tv, label: 'TV' },
  { id: 'laptop', icon: Laptop, label: 'Computer' },
  { id: 'wallet', icon: Wallet, label: 'Finanze' },
  { id: 'building', icon: Building2, label: 'Ufficio' },
  { id: 'school', icon: GraduationCap, label: 'Scuola' },
  { id: 'doctor', icon: Stethoscope, label: 'Dottore' },
  { id: 'haircut', icon: Scissors, label: 'Parrucchiere' },
  { id: 'art', icon: Paintbrush, label: 'Arte' },
  { id: 'repair', icon: Wrench, label: 'Riparazione' },
];

// Helper to get icon component by id
const getIconById = (iconId) => {
  const found = AVAILABLE_ICONS.find(i => i.id === iconId);
  return found ? found.icon : CalendarIcon;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'confirmed': return 'bg-emerald-100 text-emerald-700';
    case 'pending': return 'bg-amber-100 text-amber-700';
    case 'completed': return 'bg-gray-100 text-gray-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'high': return 'bg-red-100 text-red-700 border-red-300';
    case 'medium': return 'bg-amber-100 text-amber-700 border-amber-300';
    case 'low': return 'bg-blue-100 text-blue-700 border-blue-300';
    default: return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 ora' },
  { value: 90, label: '1h 30min' },
  { value: 120, label: '2 ore' },
  { value: 180, label: '3 ore' },
  { value: 240, label: '4 ore' },
  { value: 'allday', label: 'Tutto il giorno' },
  { value: 'custom', label: 'Personalizzato' }
];

// Sortable Task Item Component
const SortableTaskItem = ({ task, category, onToggle, onEdit, onDelete, getCategoryColorClasses }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const TaskIcon = task.icon ? getIconById(task.icon) : Star;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-xl p-4 shadow-sm transition-all ${task.done ? 'opacity-60' : ''} ${isDragging ? 'shadow-lg ring-2 ring-emerald-400' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div {...attributes} {...listeners} className="touch-none cursor-grab active:cursor-grabbing p-1 -ml-1">
          <GripVertical className="w-5 h-5 text-gray-300 flex-shrink-0" />
        </div>
        <button onClick={() => onToggle(task.id)}
          className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${task.done ? 'bg-emerald-500' : task.priority === 'high' ? 'bg-red-100' : task.priority === 'medium' ? 'bg-amber-100' : 'bg-gray-100'}`}>
          {task.done ? <Check className="w-4 h-4 text-white" /> : <TaskIcon className={`w-4 h-4 ${task.priority === 'high' ? 'text-red-500' : task.priority === 'medium' ? 'text-amber-500' : 'text-gray-500'}`} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${task.done ? 'line-through text-gray-400' : 'text-gray-900 font-medium'}`}>{task.title}</span>
            {task.priority === 'high' && !task.done && (
              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Urgente
              </span>
            )}
            {category && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColorClasses(category.color).bgLight} ${getCategoryColorClasses(category.color).text}`}>
                {category.name}
              </span>
            )}
          </div>
          {task.description && <p className="text-sm text-gray-500 mt-1">{task.description}</p>}
          {task.dueDate && (
            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <CalendarIcon className="w-3 h-3" /> Scadenza: {new Date(task.dueDate).toLocaleDateString('it-IT')}
            </p>
          )}
        </div>
        <div className="flex gap-1">
          <button onClick={() => onEdit(task)} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <Edit3 className="w-4 h-4 text-gray-500" />
          </button>
          <button onClick={() => onDelete(task.id)} className="p-1.5 hover:bg-red-50 rounded-lg">
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Sortable Category Item Component
const SortableCategoryItem = ({ category, taskCount, onEdit, onDelete, canDelete, getCategoryColorClasses }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 bg-gray-50 rounded-xl p-3 transition-all ${isDragging ? 'shadow-lg ring-2 ring-emerald-400 bg-white' : ''}`}
    >
      <div {...attributes} {...listeners} className="touch-none cursor-grab active:cursor-grabbing">
        <GripVertical className="w-5 h-5 text-gray-300" />
      </div>
      <div className={`w-4 h-4 rounded-full ${getCategoryColorClasses(category.color).bg}`} />
      <span className="flex-1 font-medium text-gray-900">{category.name}</span>
      <span className="text-xs text-gray-400">{taskCount} task</span>
      <button onClick={() => onEdit(category)} className="p-1.5 hover:bg-gray-200 rounded-lg">
        <Edit3 className="w-4 h-4 text-gray-500" />
      </button>
      {canDelete && (
        <button onClick={() => onDelete(category.id)} className="p-1.5 hover:bg-red-50 rounded-lg">
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      )}
    </div>
  );
};

// Task Overlay for dragging
const TaskDragOverlay = ({ task, category, getCategoryColorClasses }) => {
  if (!task) return null;
  const TaskIcon = task.icon ? getIconById(task.icon) : Star;
  return (
    <div className="bg-white rounded-xl p-4 shadow-2xl ring-2 ring-emerald-500 opacity-95">
      <div className="flex items-start gap-3">
        <GripVertical className="w-5 h-5 text-emerald-500 flex-shrink-0" />
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${task.done ? 'bg-emerald-500' : task.priority === 'high' ? 'bg-red-100' : task.priority === 'medium' ? 'bg-amber-100' : 'bg-gray-100'}`}>
          {task.done ? <Check className="w-4 h-4 text-white" /> : <TaskIcon className={`w-4 h-4 ${task.priority === 'high' ? 'text-red-500' : task.priority === 'medium' ? 'text-amber-500' : 'text-gray-500'}`} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${task.done ? 'line-through text-gray-400' : 'text-gray-900 font-medium'}`}>{task.title}</span>
            {category && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColorClasses(category.color).bgLight} ${getCategoryColorClasses(category.color).text}`}>
                {category.name}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Category Overlay for dragging
const CategoryDragOverlay = ({ category, taskCount, getCategoryColorClasses }) => {
  if (!category) return null;
  return (
    <div className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-2xl ring-2 ring-emerald-500">
      <GripVertical className="w-5 h-5 text-emerald-500" />
      <div className={`w-4 h-4 rounded-full ${getCategoryColorClasses(category.color).bg}`} />
      <span className="flex-1 font-medium text-gray-900">{category.name}</span>
      <span className="text-xs text-gray-400">{taskCount} task</span>
    </div>
  );
};

const CalendarPage = ({ onNavigate }) => {
  const { t } = useI18n();
  const { calendarItems, setCalendarItems, userMode, selectedOffice } = useApp();
  const isPartner = userMode === 'partner';
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [activeView, setActiveView] = useState('calendar');
  
  // Dynamic office configuration from context
  const officeId = selectedOffice?.id || 1;
  const officeModules = selectedOffice?.enabledModules || ['appointments'];
  
  // Get resources dynamically based on office
  const resourceInfo = useMemo(() => {
    if (!isPartner) return { type: null, list: [] };
    return getResourcesForOffice(officeId);
  }, [isPartner, officeId]);
  
  const resources = resourceInfo.list.filter(r => r.isActive !== false);
  const officeConfig = useMemo(() => {
    if (!isPartner) return null;
    // Determina tipo risorsa dal primo modulo abilitato che richiede risorse
    const moduleWithResources = officeModules.find(m => MODULES[m]?.schema?.resourceTypes?.length > 0);
    const moduleConfig = MODULES[moduleWithResources] || {};
    return {
      resourceType: moduleConfig.schema?.resourceTypes?.[0],
      resourceName: 'Risorsa',
      resourceNamePlural: 'Risorse',
      bookingType: 'appointment',
      bookingName: 'Prenotazione',
    };
  }, [isPartner, officeModules]);
  
  // Get bookings dynamically for current office
  const [receivedBookings, setReceivedBookings] = useState(() => {
    if (!isPartner) return [];
    return getAllBookingsForOffice(officeId);
  });
  
  // Filter state for resources (operator, table, room)
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  
  // Filtered bookings based on selected resource
  const filteredBookings = useMemo(() => {
    if (!selectedResourceId) return receivedBookings;
    const resType = officeConfig?.resourceType;
    const resourceKey = resType === 'operator' ? 'operatorId' :
                       resType === 'table' ? 'tableId' :
                       resType === 'room' ? 'roomId' :
                       resType === 'item' ? 'itemId' :
                       resType === 'trainer' ? 'trainerId' :
                       resType === 'space' ? 'spaceId' : null;
    if (!resourceKey) return receivedBookings;
    return receivedBookings.filter(b => b[resourceKey] === selectedResourceId);
  }, [receivedBookings, selectedResourceId, officeConfig]);
  
  const updateBookingStatus = (bookingId, newStatus) => {
    setReceivedBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
  };
  
  // Categories state
  const [categories, setCategories] = useState([
    { id: 'personal', name: 'Personale', color: 'purple' },
    { id: 'work', name: 'Lavoro', color: 'blue' },
    { id: 'health', name: 'Salute', color: 'green' },
    { id: 'shopping', name: 'Shopping', color: 'pink' },
    { id: 'finance', name: 'Finanze', color: 'amber' },
    { id: 'other', name: 'Altro', color: 'gray' }
  ]);
  
  // Tasks state
  const [tasks, setTasks] = useState([
    { id: 1, title: 'Comprare il latte', description: 'Dal supermercato vicino casa', done: false, priority: 'low', category: 'shopping', dueDate: '2026-01-11', order: 0, icon: 'shopping' },
    { id: 2, title: 'Chiamare mamma', description: 'Per il compleanno di papà', done: true, priority: 'medium', category: 'personal', dueDate: '2026-01-10', order: 1, icon: 'phone' },
    { id: 3, title: 'Pagare bollette', description: 'Luce e gas', done: false, priority: 'high', category: 'finance', dueDate: '2026-01-15', order: 2, icon: 'wallet' },
    { id: 4, title: 'Preparare presentazione', description: 'Per meeting di lunedì', done: false, priority: 'high', category: 'work', dueDate: '2026-01-13', order: 3, icon: 'laptop' }
  ]);
  
  // Modal states
  const [showItemModal, setShowItemModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Form states
  const [itemForm, setItemForm] = useState({ title: '', description: '', date: '', time: '', duration: 60, customDuration: '', location: '', priority: 'medium', icon: 'calendar' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'medium', category: 'personal', dueDate: '', icon: 'star' });
  const [categoryForm, setCategoryForm] = useState({ name: '', color: 'purple' });
  
  // Task filter
  const [taskFilter, setTaskFilter] = useState('all');

  const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1).getDay();
  
  const weekDays = [t('calendar', 'sun'), t('calendar', 'mon'), t('calendar', 'tue'), t('calendar', 'wed'), t('calendar', 'thu'), t('calendar', 'fri'), t('calendar', 'sat')];
  const months = [
    t('calendar', 'january'), t('calendar', 'february'), t('calendar', 'march'), t('calendar', 'april'),
    t('calendar', 'may'), t('calendar', 'june'), t('calendar', 'july'), t('calendar', 'august'),
    t('calendar', 'september'), t('calendar', 'october'), t('calendar', 'november'), t('calendar', 'december')
  ];

  const CATEGORY_COLORS = ['purple', 'blue', 'green', 'pink', 'amber', 'red', 'cyan', 'orange', 'indigo', 'gray'];

  // Static color mapping for Tailwind (dynamic classes don't work at runtime)
  const getCategoryColorClasses = (color) => {
    const colorMap = {
      purple: { bg: 'bg-purple-500', bgLight: 'bg-purple-100', text: 'text-purple-600' },
      blue: { bg: 'bg-blue-500', bgLight: 'bg-blue-100', text: 'text-blue-600' },
      green: { bg: 'bg-green-500', bgLight: 'bg-green-100', text: 'text-green-600' },
      pink: { bg: 'bg-pink-500', bgLight: 'bg-pink-100', text: 'text-pink-600' },
      amber: { bg: 'bg-amber-500', bgLight: 'bg-amber-100', text: 'text-amber-600' },
      red: { bg: 'bg-red-500', bgLight: 'bg-red-100', text: 'text-red-600' },
      cyan: { bg: 'bg-cyan-500', bgLight: 'bg-cyan-100', text: 'text-cyan-600' },
      orange: { bg: 'bg-orange-500', bgLight: 'bg-orange-100', text: 'text-orange-600' },
      indigo: { bg: 'bg-indigo-500', bgLight: 'bg-indigo-100', text: 'text-indigo-600' },
      gray: { bg: 'bg-gray-500', bgLight: 'bg-gray-100', text: 'text-gray-600' },
    };
    return colorMap[color] || colorMap.gray;
  };

  const getStatusText = (status) => {
    if (status === 'confirmed') return t('orders', 'confirmed');
    if (status === 'pending') return t('orders', 'pending');
    if (status === 'completed') return t('orders', 'completed');
    return status;
  };

  const formatDuration = (duration) => {
    if (duration === 'allday') return 'Tutto il giorno';
    if (duration >= 60) {
      const hours = Math.floor(duration / 60);
      const mins = duration % 60;
      return mins > 0 ? `${hours}h ${mins}min` : `${hours} or${hours > 1 ? 'e' : 'a'}`;
    }
    return `${duration} min`;
  };

  // Get bookings with business info
  const bookingsWithInfo = BOOKINGS.map(booking => {
    const business = OFFICES.find(b => b.id === booking.officeId);
    const service = SERVICES.find(s => s.id === booking.serviceId);
    return { ...booking, business, service };
  });

  const formatDateForComparison = (year, month, day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const selectedDateString = formatDateForComparison(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDay);
  const selectedDayBookings = bookingsWithInfo.filter(b => b.date === selectedDateString);
  const selectedDayItems = calendarItems.filter(e => e.date === selectedDateString);
  
  // Combine bookings and items into a single sorted list
  const allDayEvents = [
    ...selectedDayBookings.map(b => ({ ...b, type: 'booking' })),
    ...selectedDayItems.map(i => ({ ...i, type: 'item' }))
  ].sort((a, b) => a.time.localeCompare(b.time));

  // Count of events for the SELECTED day (total, not just pending)
  const getItemsCount = () => {
    return selectedDayBookings.length + selectedDayItems.length;
  };

  // Count of ALL tasks (not just pending)
  const getTotalTasksCount = () => tasks.length;

  const getDaysWithItems = () => {
    const bookingDays = bookingsWithInfo
      .filter(b => {
        const d = new Date(b.date);
        return d.getMonth() === selectedDate.getMonth() && d.getFullYear() === selectedDate.getFullYear();
      })
      .map(b => new Date(b.date).getDate());
    
    const itemDays = calendarItems
      .filter(e => {
        const d = new Date(e.date);
        return d.getMonth() === selectedDate.getMonth() && d.getFullYear() === selectedDate.getFullYear();
      })
      .map(e => new Date(e.date).getDate());
    
    return [...new Set([...bookingDays, ...itemDays])];
  };

  const handleDayClick = (day) => setSelectedDay(day);

  const formatDisplayDate = () => {
    const dayNames = [
      t('days', 'sunday'), t('days', 'monday'), t('days', 'tuesday'), 
      t('days', 'wednesday'), t('days', 'thursday'), t('days', 'friday'), t('days', 'saturday')
    ];
    const date = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDay);
    return `${dayNames[date.getDay()]}, ${selectedDay} ${months[selectedDate.getMonth()]}`;
  };

  // Calendar Item CRUD
  const openAddItem = () => {
    setEditingItem(null);
    setItemForm({ title: '', description: '', date: selectedDateString, time: '09:00', duration: 60, customDuration: '', location: '', priority: 'medium', icon: 'calendar' });
    setShowItemModal(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    const isCustom = !DURATION_OPTIONS.find(d => d.value === item.duration);
    setItemForm({ 
      ...item, 
      duration: isCustom ? 'custom' : item.duration,
      customDuration: isCustom ? item.duration : '',
      icon: item.icon || 'calendar'
    });
    setShowItemModal(true);
  };

  const saveItem = () => {
    if (!itemForm.title.trim()) return;
    const duration = itemForm.duration === 'custom' ? parseInt(itemForm.customDuration) || 60 : itemForm.duration;
    const itemData = { ...itemForm, duration };
    
    if (editingItem) {
      setCalendarItems(calendarItems.map(e => e.id === editingItem.id ? { ...itemData, id: editingItem.id } : e));
    } else {
      setCalendarItems([...calendarItems, { ...itemData, id: Date.now() }]);
    }
    setShowItemModal(false);
  };

  const deleteItem = (id) => setCalendarItems(calendarItems.filter(e => e.id !== id));

  // Task CRUD
  const openAddTask = () => {
    setEditingTask(null);
    setTaskForm({ title: '', description: '', priority: 'medium', category: categories[0]?.id || 'personal', dueDate: selectedDateString, icon: 'star' });
    setShowTaskModal(true);
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({ title: task.title, description: task.description || '', priority: task.priority, category: task.category, dueDate: task.dueDate || '', icon: task.icon || 'star' });
    setShowTaskModal(true);
  };

  const saveTask = () => {
    if (!taskForm.title.trim()) return;
    if (editingTask) {
      setTasks(tasks.map(t => t.id === editingTask.id ? { ...t, ...taskForm } : t));
    } else {
      const maxOrder = Math.max(...tasks.map(t => t.order || 0), -1);
      setTasks([...tasks, { ...taskForm, id: Date.now(), done: false, order: maxOrder + 1 }]);
    }
    setShowTaskModal(false);
  };

  const deleteTask = (id) => setTasks(tasks.filter(t => t.id !== id));
  const toggleTask = (id) => setTasks(tasks.map(t => t.id === id ? { ...t, done: !t.done } : t));

  // Category CRUD
  const openAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', color: 'purple' });
    setShowCategoryModal(true);
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name, color: cat.color });
    setShowCategoryModal(true);
  };

  const saveCategory = () => {
    if (!categoryForm.name.trim()) return;
    if (editingCategory) {
      setCategories(categories.map(c => c.id === editingCategory.id ? { ...c, ...categoryForm } : c));
      // Update tasks with old category id
      if (categoryForm.name !== editingCategory.name) {
        setTasks(tasks.map(t => t.category === editingCategory.id ? { ...t, category: editingCategory.id } : t));
      }
    } else {
      const newId = categoryForm.name.toLowerCase().replace(/\s+/g, '-');
      setCategories([...categories, { ...categoryForm, id: newId }]);
    }
    setShowCategoryModal(false);
  };

  const deleteCategory = (id) => {
    if (categories.length <= 1) return;
    setCategories(categories.filter(c => c.id !== id));
    // Move tasks to first remaining category
    const firstCat = categories.find(c => c.id !== id);
    setTasks(tasks.map(t => t.category === id ? { ...t, category: firstCat?.id } : t));
  };

  // DnD-kit sensors for touch and pointer
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle task drag end
  const handleTaskDragEnd = (event) => {
    const { active, over } = event;
    setActiveTaskId(null);
    
    if (active.id !== over?.id) {
      setTasks((items) => {
        const oldIndex = items.findIndex(t => t.id === active.id);
        const newIndex = items.findIndex(t => t.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update order
        return newItems.map((t, i) => ({ ...t, order: i }));
      });
    }
  };

  // Handle category drag end
  const handleCategoryDragEnd = (event) => {
    const { active, over } = event;
    setActiveCategoryId(null);
    
    if (active.id !== over?.id) {
      setCategories((items) => {
        const oldIndex = items.findIndex(c => c.id === active.id);
        const newIndex = items.findIndex(c => c.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Active dragging states for overlay
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  
  const activeTask = activeTaskId ? tasks.find(t => t.id === activeTaskId) : null;
  const activeCategory = activeCategoryId ? categories.find(c => c.id === activeCategoryId) : null;

  // Filter and sort tasks
  const filteredTasks = (taskFilter === 'all' ? tasks : tasks.filter(t => t.category === taskFilter))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const daysWithItems = getDaysWithItems();

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 pt-6 pb-6">
        <div className="max-w-screen-2xl mx-auto px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-shrink-0">
              <h1 className="text-2xl lg:text-3xl font-bold text-white">
                {isPartner ? 'Agenda' : t('calendar', 'agenda')}
              </h1>
              <p className="text-emerald-100 text-sm lg:text-base whitespace-nowrap">
                {isPartner ? 'Le tue prenotazioni' : t('calendar', 'manageCommitments')}
              </p>
            </div>

            <div className="flex bg-white/20 backdrop-blur rounded-xl p-1 flex-shrink-0">
              <button
                onClick={() => setActiveView('calendar')}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeView === 'calendar' ? 'bg-white text-emerald-600' : 'text-white'}`}
              >
                {t('calendar', 'calendar')}
                {(isPartner ? receivedBookings.filter(b => b.status !== 'completed').length : getItemsCount()) > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeView === 'calendar' ? 'bg-emerald-100 text-emerald-600' : 'bg-white/20 text-white'}`}>
                    {isPartner ? receivedBookings.filter(b => b.status !== 'completed').length : getItemsCount()}
                  </span>
                )}
              </button>
              {!isPartner && (
                <button
                  onClick={() => setActiveView('tasks')}
                  className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeView === 'tasks' ? 'bg-white text-emerald-600' : 'text-white'}`}
                >
                  {t('calendar', 'tasks')}
                  {getTotalTasksCount() > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeView === 'tasks' ? 'bg-emerald-100 text-emerald-600' : 'bg-white/20 text-white'}`}>
                      {getTotalTasksCount()}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 py-4 lg:py-6">
        {/* Partner Mode: Received Bookings View */}
        {isPartner && activeView === 'calendar' && (
          <div className="space-y-6">
            {/* Resource Filter (Operators/Tables/Rooms) */}
            {resources.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  {officeConfig?.resourceType === 'operator' && <Users className="w-5 h-5 text-emerald-600" />}
                  {officeConfig?.resourceType === 'table' && <UtensilsCrossed className="w-5 h-5 text-emerald-600" />}
                  {officeConfig?.resourceType === 'room' && <Bed className="w-5 h-5 text-emerald-600" />}
                  {officeConfig?.resourceType === 'item' && <Car className="w-5 h-5 text-emerald-600" />}
                  <h3 className="font-semibold text-gray-900">{officeConfig?.resourceNamePlural || 'Risorse'}</h3>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  <button
                    onClick={() => setSelectedResourceId(null)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      !selectedResourceId ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Tutti
                  </button>
                  {resources.map((resource) => (
                    <button
                      key={resource.id}
                      onClick={() => setSelectedResourceId(resource.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                        selectedResourceId === resource.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {resource.avatar ? (
                        <span className={`w-6 h-6 rounded-full bg-${resource.color || 'gray'}-500 text-white text-xs flex items-center justify-center`}>
                          {resource.avatar}
                        </span>
                      ) : null}
                      <span>{resource.name}</span>
                      {resource.role && <span className="text-xs opacity-70">({resource.role})</span>}
                      {resource.seats && <span className="text-xs opacity-70">({resource.seats} posti)</span>}
                      {resource.type && <span className="text-xs opacity-70">({resource.type})</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Today's Bookings Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div 
                className="bg-white rounded-xl p-4 shadow-sm text-center cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onNavigate && onNavigate('calendar', { filter: 'confirmed' })}
                data-testid="stats-confirmed"
              >
                <p className="text-2xl font-bold text-emerald-600">{filteredBookings.filter(b => b.status === 'confirmed').length}</p>
                <p className="text-sm text-gray-500">Confermate</p>
              </div>
              <div 
                className="bg-white rounded-xl p-4 shadow-sm text-center cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onNavigate && onNavigate('calendar', { filter: 'pending' })}
                data-testid="stats-pending"
              >
                <p className="text-2xl font-bold text-amber-600">{filteredBookings.filter(b => b.status === 'pending').length}</p>
                <p className="text-sm text-gray-500">In attesa</p>
              </div>
              <div 
                className="bg-white rounded-xl p-4 shadow-sm text-center cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onNavigate && onNavigate('calendar', { filter: 'completed' })}
                data-testid="stats-completed"
              >
                <p className="text-2xl font-bold text-gray-600">{filteredBookings.filter(b => b.status === 'completed').length}</p>
                <p className="text-sm text-gray-500">Completate</p>
              </div>
            </div>

            {/* Bookings List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">
                  {officeConfig?.bookingType === 'stay' ? 'Check-in di Oggi' : 'Prenotazioni di Oggi'}
                </h3>
                {selectedResourceId && (
                  <span className="text-sm text-emerald-600">
                    Filtro: {resources.find(r => r.id === selectedResourceId)?.name}
                  </span>
                )}
              </div>
              {filteredBookings.filter(b => b.status !== 'completed').length > 0 ? (
                filteredBookings.filter(b => b.status !== 'completed').map((booking) => (
                  <div 
                    key={booking.id} 
                    className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => onNavigate && onNavigate('booking-detail', { id: booking.id })}
                    data-testid={`booking-${booking.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0 ${
                        booking.isElit ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-emerald-500'
                      }`}>
                        {booking.clientAvatar || booking.clientName?.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-gray-900">{booking.clientName}</h4>
                          {booking.isElit && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">ELIT</span>}
                        </div>
                        <p className="text-sm text-gray-600">
                          {booking.serviceName || booking.service || (booking.roomName ? `${booking.roomName} - ${booking.roomType}` : booking.tableName)}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" /> {booking.time || (booking.checkIn ? `Check-in: ${booking.checkIn}` : '')}
                          </span>
                          {booking.duration && <span>{booking.duration} min</span>}
                          {booking.guests && <span>{booking.guests} ospiti</span>}
                          {booking.nights && <span>{booking.nights} notti</span>}
                          {booking.operatorName && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" /> {booking.operatorName}
                            </span>
                          )}
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusColor(booking.status)}`}>
                            {getStatusText(booking.status)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-lg text-gray-900">€{booking.price || booking.totalPrice}</p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    {booking.status === 'pending' && (
                      <div className="flex gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                          className="flex-1 bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-emerald-600"
                          data-testid={`confirm-booking-${booking.id}`}
                        >
                          Conferma
                        </button>
                        <button 
                          onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                          className="flex-1 bg-red-50 text-red-600 py-2.5 rounded-xl text-sm font-medium hover:bg-red-100"
                          data-testid={`reject-booking-${booking.id}`}
                        >
                          Rifiuta
                        </button>
                      </div>
                    )}
                    {booking.status === 'confirmed' && (
                      <button 
                        onClick={() => updateBookingStatus(booking.id, 'completed')}
                        className="w-full mt-4 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-200"
                      >
                        Segna come completato
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-xl p-8 shadow-sm text-center">
                  <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-900 mb-1">Nessuna prenotazione attiva</h4>
                  <p className="text-sm text-gray-500">Le nuove prenotazioni appariranno qui</p>
                </div>
              )}
            </div>

            {/* Completed Bookings */}
            {filteredBookings.filter(b => b.status === 'completed').length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-500 text-sm">Completate</h3>
                {filteredBookings.filter(b => b.status === 'completed').map((booking) => (
                  <div 
                    key={booking.id} 
                    className="bg-white rounded-xl p-4 shadow-sm opacity-60 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => onNavigate && onNavigate('booking-detail', { id: booking.id })}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white bg-gray-400 flex-shrink-0">
                        {booking.clientAvatar || booking.clientName?.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900">{booking.clientName}</h4>
                        <p className="text-sm text-gray-500">{booking.serviceName || booking.service} • {booking.time}</p>
                      </div>
                      <p className="font-bold text-gray-700">€{booking.price || booking.totalPrice}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Consumer Mode: Original Calendar View */}
        {!isPartner && activeView === 'calendar' && (
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Calendar */}
            <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm p-4 lg:p-6">
              <div className="flex items-center justify-between mb-4 lg:mb-6">
                <button onClick={() => { setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1)); setSelectedDay(1); }} className="p-2 hover:bg-gray-100 rounded-full">
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="font-semibold text-gray-900 lg:text-lg">{months[selectedDate.getMonth()]} {selectedDate.getFullYear()}</h2>
                <button onClick={() => { setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1)); setSelectedDay(1); }} className="p-2 hover:bg-gray-100 rounded-full">
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-xs lg:text-sm text-gray-500 font-medium py-2">{day}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1 lg:gap-2">
                {[...Array(firstDayOfMonth)].map((_, i) => <div key={`empty-${i}`} className="aspect-square" />)}
                {[...Array(daysInMonth)].map((_, i) => {
                  const day = i + 1;
                  const isSelected = day === selectedDay;
                  const hasItem = daysWithItems.includes(day);
                  return (
                    <button key={day} onClick={() => handleDayClick(day)}
                      className={`aspect-square rounded-xl lg:rounded-2xl flex flex-col items-center justify-center text-sm lg:text-base relative transition-all ${isSelected ? 'bg-emerald-500 text-white font-bold shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-400 ring-offset-1' : 'text-gray-700 hover:bg-gray-100'}`}>
                      {day}
                      {hasItem && !isSelected && <div className="absolute bottom-1 lg:bottom-2 w-1.5 h-1.5 bg-emerald-500 rounded-full" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected Day's Items */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 lg:text-lg">{formatDisplayDate()}</h3>
                <button onClick={openAddItem} className="flex items-center gap-1 text-emerald-600 text-sm font-medium hover:text-emerald-700">
                  <Plus className="w-4 h-4" /> Aggiungi
                </button>
              </div>
              
              <div className="space-y-4">
                {allDayEvents.map(event => (
                  event.type === 'booking' ? (
                    <div key={`booking-${event.id}`} className="bg-white rounded-xl lg:rounded-2xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => onNavigate('booking-detail', { id: event.id })}>
                      <div className="flex gap-3">
                        <img src={event.business?.image} alt={event.business?.name} className="w-16 h-16 rounded-xl object-cover" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-semibold text-gray-900 truncate">{event.service?.name}</h4>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${getStatusColor(event.status)}`}>
                              {getStatusText(event.status)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate">{event.business?.name}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{event.time}</span>
                            <span className="font-semibold text-gray-900">€{event.service?.price || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={`item-${event.id}`} className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-emerald-500">
                      <div className="flex items-start gap-3">
                        {(() => {
                          const EventIcon = event.icon ? getIconById(event.icon) : CalendarIcon;
                          return (
                            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <EventIcon className="w-5 h-5 text-emerald-600" />
                            </div>
                          );
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-gray-900">{event.title}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(event.priority)}`}>
                              {event.priority === 'high' ? 'Alta' : event.priority === 'medium' ? 'Media' : 'Bassa'}
                            </span>
                          </div>
                          {event.description && <p className="text-sm text-gray-500 mt-1">{event.description}</p>}
                          <div className="flex items-center gap-3 mt-2 text-sm text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{event.time} ({formatDuration(event.duration)})</span>
                            {event.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <button onClick={() => openEditItem(event)} className="p-1.5 hover:bg-gray-100 rounded-lg"><Edit3 className="w-4 h-4 text-gray-500" /></button>
                          <button onClick={() => deleteItem(event.id)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                        </div>
                      </div>
                    </div>
                  )
                ))}
                
                {allDayEvents.length === 0 && (
                  <div className="text-center py-8 text-gray-500 bg-white rounded-xl">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>{t('calendar', 'noEventsToday')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tasks View (Consumer Only) */}
        {!isPartner && activeView === 'tasks' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 text-lg">{t('calendar', 'myTasks')}</h3>
              <div className="flex gap-2">
                <button onClick={() => setShowCategoryManager(true)} className="flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
                  <Settings className="w-4 h-4" />
                </button>
                <button onClick={openAddTask} className="flex items-center gap-1 bg-emerald-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors">
                  <Plus className="w-4 h-4" /> Nuova Task
                </button>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              <button onClick={() => setTaskFilter('all')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${taskFilter === 'all' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                Tutte ({tasks.length})
              </button>
              {categories.map(cat => {
                const count = tasks.filter(t => t.category === cat.id).length;
                return (
                  <button key={cat.id} onClick={() => setTaskFilter(cat.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${taskFilter === cat.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {cat.name} ({count})
                  </button>
                );
              })}
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={(event) => setActiveTaskId(event.active.id)}
              onDragEnd={handleTaskDragEnd}
            >
              <SortableContext items={filteredTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {filteredTasks.map(task => {
                    const category = categories.find(c => c.id === task.category);
                    return (
                      <SortableTaskItem
                        key={task.id}
                        task={task}
                        category={category}
                        onToggle={toggleTask}
                        onEdit={openEditTask}
                        onDelete={deleteTask}
                        getCategoryColorClasses={getCategoryColorClasses}
                      />
                    );
                  })}
                  
                  {filteredTasks.length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-white rounded-xl">
                      <Tag className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Nessuna task in questa categoria</p>
                    </div>
                  )}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeTask && (
                  <TaskDragOverlay
                    task={activeTask}
                    category={categories.find(c => c.id === activeTask.category)}
                    getCategoryColorClasses={getCategoryColorClasses}
                  />
                )}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </div>

      {/* Calendar Item Modal */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingItem ? 'Modifica' : 'Nuovo Impegno'}</h3>
              <button onClick={() => setShowItemModal(false)} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
                <input type="text" value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                  placeholder="Es. Appuntamento, Riunione, Servizio..." className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <textarea value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  placeholder="Dettagli..." rows={2} className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                  <input type="date" value={itemForm.date} onChange={(e) => setItemForm({ ...itemForm, date: e.target.value })}
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ora</label>
                  <input type="time" value={itemForm.time} onChange={(e) => setItemForm({ ...itemForm, time: e.target.value })}
                    className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Durata</label>
                <select value={itemForm.duration} onChange={(e) => setItemForm({ ...itemForm, duration: e.target.value === 'custom' || e.target.value === 'allday' ? e.target.value : parseInt(e.target.value) })}
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500">
                  {DURATION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {itemForm.duration === 'custom' && (
                  <input type="number" value={itemForm.customDuration} onChange={(e) => setItemForm({ ...itemForm, customDuration: e.target.value })}
                    placeholder="Minuti" min="1" className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500 mt-2" />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Luogo</label>
                <input type="text" value={itemForm.location} onChange={(e) => setItemForm({ ...itemForm, location: e.target.value })}
                  placeholder="Es. Ufficio, Casa, Ristorante..." className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priorità</label>
                <div className="flex gap-2">
                  {['low', 'medium', 'high'].map(p => (
                    <button key={p} onClick={() => setItemForm({ ...itemForm, priority: p })}
                      className={`flex-1 py-2 rounded-xl text-sm font-medium border-2 transition-all ${itemForm.priority === p ? getPriorityColor(p) : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                      {p === 'high' ? 'Alta' : p === 'medium' ? 'Media' : 'Bassa'}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icona</label>
                <div className="grid grid-cols-6 gap-2 max-h-32 overflow-y-auto p-1">
                  {AVAILABLE_ICONS.map(({ id, icon: Icon, label }) => (
                    <button key={id} onClick={() => setItemForm({ ...itemForm, icon: id })} title={label}
                      className={`p-2.5 rounded-xl transition-all ${itemForm.icon === id ? 'bg-emerald-500 text-white ring-2 ring-emerald-400 ring-offset-1' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      <Icon className="w-5 h-5 mx-auto" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <button onClick={saveItem} disabled={!itemForm.title.trim()}
              className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 mt-6">
              {editingItem ? 'Salva Modifiche' : 'Aggiungi'}
            </button>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingTask ? 'Modifica Task' : 'Nuova Task'}</h3>
              <button onClick={() => setShowTaskModal(false)} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
                <input type="text" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Cosa devi fare?" className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Dettagli aggiuntivi..." rows={2} className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scadenza</label>
                <input type="date" value={taskForm.dueDate} onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                  className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                <div className="grid grid-cols-3 gap-2">
                  {categories.map(cat => (
                    <button key={cat.id} onClick={() => setTaskForm({ ...taskForm, category: cat.id })}
                      className={`py-2 rounded-xl text-sm font-medium border-2 transition-all ${taskForm.category === cat.id ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priorità</label>
                <div className="flex gap-2">
                  {['low', 'medium', 'high'].map(p => (
                    <button key={p} onClick={() => setTaskForm({ ...taskForm, priority: p })}
                      className={`flex-1 py-2 rounded-xl text-sm font-medium border-2 transition-all ${taskForm.priority === p ? getPriorityColor(p) : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                      {p === 'high' ? 'Alta' : p === 'medium' ? 'Media' : 'Bassa'}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Icona</label>
                <div className="grid grid-cols-6 gap-2 max-h-32 overflow-y-auto p-1">
                  {AVAILABLE_ICONS.map(({ id, icon: Icon, label }) => (
                    <button key={id} onClick={() => setTaskForm({ ...taskForm, icon: id })} title={label}
                      className={`p-2.5 rounded-xl transition-all ${taskForm.icon === id ? 'bg-emerald-500 text-white ring-2 ring-emerald-400 ring-offset-1' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      <Icon className="w-5 h-5 mx-auto" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <button onClick={saveTask} disabled={!taskForm.title.trim()}
              className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 mt-6">
              {editingTask ? 'Salva Modifiche' : 'Aggiungi Task'}
            </button>
          </div>
        </div>
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Gestisci Categorie</h3>
              <button onClick={() => setShowCategoryManager(false)} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            
            <p className="text-sm text-gray-500 mb-4">Trascina per riordinare le categorie</p>
            
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={(event) => setActiveCategoryId(event.active.id)}
              onDragEnd={handleCategoryDragEnd}
            >
              <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2 mb-4">
                  {categories.map(cat => (
                    <SortableCategoryItem
                      key={cat.id}
                      category={cat}
                      taskCount={tasks.filter(t => t.category === cat.id).length}
                      onEdit={openEditCategory}
                      onDelete={deleteCategory}
                      canDelete={categories.length > 1}
                      getCategoryColorClasses={getCategoryColorClasses}
                    />
                  ))}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeCategory && (
                  <CategoryDragOverlay
                    category={activeCategory}
                    taskCount={tasks.filter(t => t.category === activeCategory.id).length}
                    getCategoryColorClasses={getCategoryColorClasses}
                  />
                )}
              </DragOverlay>
            </DndContext>
            
            <button onClick={openAddCategory} className="w-full bg-emerald-500 text-white py-3 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2">
              <Plus className="w-5 h-5" /> Nuova Categoria
            </button>
          </div>
        </div>
      )}

      {/* Category Add/Edit Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingCategory ? 'Modifica Categoria' : 'Nuova Categoria'}</h3>
              <button onClick={() => setShowCategoryModal(false)} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input type="text" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="Es. Famiglia, Sport..." className="w-full bg-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Colore</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_COLORS.map(color => (
                    <button key={color} onClick={() => setCategoryForm({ ...categoryForm, color })}
                      className={`w-10 h-10 rounded-xl ${getCategoryColorClasses(color).bg} transition-all ${categoryForm.color === color ? 'ring-2 ring-offset-2 ring-gray-900 scale-110' : 'hover:scale-105'}`} />
                  ))}
                </div>
              </div>
            </div>
            
            <button onClick={saveCategory} disabled={!categoryForm.name.trim()}
              className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 mt-6">
              {editingCategory ? 'Salva' : 'Crea Categoria'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
